import type { components } from "@/src/api/api";
import type { Translations } from "@/src/i18n/translations";

/**
 * Charge utile de `GET /api/v1/moderation/post`.
 *
 * ⚠️ `data` est **facultatif** dans le contrat — le `NonNullable` est ce qui
 * permet d'en dériver le reste sans que tout devienne `| undefined`.
 */
type ModerationPostsPayload = NonNullable<
  components["schemas"]["ModerationPostPageApiSchema"]
>;

/**
 * Une publication, vue par la modération.
 *
 * ⚠️ **C'est la seule forme de l'API qui porte `moderationScores`.** L'API
 * mobile ne l'expose nulle part, et à dessein : la réponse brute du classifieur
 * dirait à un auteur curieux quel seuil viser pour passer sous le radar. Ce qui
 * s'affiche depuis ce type ne doit donc jamais ressortir dans un composant
 * partagé avec un écran nourri par les routes publiques.
 */
export type ModerationPost = ModerationPostsPayload["posts"][number];

export type PostPhoto = ModerationPost["photos"][number];

/**
 * Les quatre états d'une publication.
 *
 * ⚠️ **Leurs noms ne disent pas ce qu'ils suggèrent** :
 *
 * - `scanning` — la **machine** regarde. Quelques secondes, normalement ; une
 *   publication qui y reste est une anomalie, pas une file d'attente ;
 * - `pending_review` — **la file à relever**. Le classifieur a un doute, ou il
 *   était injoignable. C'est un humain qu'on attend, pas un traitement ;
 * - `approved` — en ligne ;
 * - `rejected` — refusée par un administrateur, invisible de tous.
 *
 * Les libellés ne recopient donc pas les codes — même parti que les statuts
 * d'événement, où `close` veut dire « joué » et `complete` « effectif au
 * complet ».
 */
export const POST_STATUSES = [
  "pending_review",
  "scanning",
  "approved",
  "rejected",
] as const;

export type PostStatus = (typeof POST_STATUSES)[number];

export function isPostStatus(value: unknown): value is PostStatus {
  return POST_STATUSES.some((status) => status === value);
}

/**
 * Le libellé de chaque statut, par sa clé de dictionnaire.
 *
 * ⚠️ **`satisfies` et non `:`** — même parti que `moderation.type.ts` : la
 * table est ainsi *vérifiée* exhaustive sans perdre ses types littéraux. Un
 * statut ajouté au contrat sans son libellé devient une erreur de `tsc`, là où
 * une annotation ordinaire l'aurait laissé passer.
 */
export const STATUS_KEY = {
  pending_review: "status-pending-review",
  scanning: "status-scanning",
  approved: "status-approved",
  rejected: "status-rejected",
} satisfies Record<PostStatus, keyof Translations["posts"]>;

/**
 * Les statuts **filtrables**, `all` compris.
 *
 * ⚠️ `all` est une valeur d'interface : elle ne part jamais au serveur, pour qui
 * `status` absent signifie déjà « les quatre ». Même parti que la liste des
 * événements.
 *
 * L'ordre n'est pas alphabétique : il va de ce qui appelle une action à ce qui
 * n'en appelle plus. « Tous » d'abord, parce que c'est désormais l'écran par
 * défaut — la file de validation n'en est qu'un filtre.
 */
export const STATUS_FILTERS = ["all", ...POST_STATUSES] as const;

export type PostStatusFilter = (typeof STATUS_FILTERS)[number];

export function isPostStatusFilter(value: unknown): value is PostStatusFilter {
  return STATUS_FILTERS.some((status) => status === value);
}

export const STATUS_FILTER_KEY = {
  all: "filter-all",
  ...STATUS_KEY,
} satisfies Record<PostStatusFilter, keyof Translations["posts"]>;

/** Les deux natures de publication, plus « les deux ». */
export const TYPE_FILTERS = ["all", "gallery", "thread"] as const;

export type PostTypeFilter = (typeof TYPE_FILTERS)[number];

export function isPostTypeFilter(value: unknown): value is PostTypeFilter {
  return TYPE_FILTERS.some((type) => type === value);
}

export const TYPE_FILTER_KEY = {
  all: "filter-all",
  gallery: "type-gallery",
  thread: "type-thread",
} satisfies Record<PostTypeFilter, keyof Translations["posts"]>;

/**
 * Que faire des publications retirées **par leur auteur**.
 *
 * ⚠️ **`all` est le défaut, côté serveur comme ici.** La suppression par un
 * auteur est douce précisément pour que la modération continue de les lire : un
 * compte qui publie et retire trois galeries douteuses en une semaine est le cas
 * qu'on veut le plus sûrement voir. `only` en fait une file à part — c'est elle
 * qui répond à « qu'a-t-il effacé depuis qu'on l'a signalé ».
 */
export const DELETED_FILTERS = ["all", "exclude", "only"] as const;

export type DeletedFilter = (typeof DELETED_FILTERS)[number];

export function isDeletedFilter(value: unknown): value is DeletedFilter {
  return DELETED_FILTERS.some((deleted) => deleted === value);
}

export const DELETED_FILTER_KEY = {
  all: "filter-all",
  exclude: "deleted-exclude",
  only: "deleted-only",
} satisfies Record<DeletedFilter, keyof Translations["posts"]>;

/**
 * Les trois tris du serveur.
 *
 * ⚠️ **`likes` et `comments` ne sont pas du confort** : la publication que
 * quatre cents personnes ont vue n'est pas le même dossier que celle que trois
 * personnes ont vue, et aucun filtre par statut ne fait ressortir la première.
 */
export const SORTS = ["createdAt", "likes", "comments"] as const;

export type PostSort = (typeof SORTS)[number];

export function isPostSort(value: unknown): value is PostSort {
  return SORTS.some((sort) => sort === value);
}

export const SORT_KEY = {
  createdAt: "sort-date",
  likes: "sort-likes",
  comments: "sort-comments",
} satisfies Record<PostSort, keyof Translations["posts"]>;

export const ORDERS = ["desc", "asc"] as const;

export type PostOrder = (typeof ORDERS)[number];

export function isPostOrder(value: unknown): value is PostOrder {
  return ORDERS.some((order) => order === value);
}

/**
 * Une publication vue depuis sa **fiche** (`GET /moderation/post/{id}`).
 *
 * Trois champs de plus que la ligne de liste, et aucun n'est décoratif :
 * `moderatedBy` (une décision anonyme n'est pas relisible), `reportCount` (une
 * galerie signalée douze fois ne se juge pas comme une publication retenue par
 * le classifieur) et `category`, résolue par le serveur.
 */
export type AdminPost = components["schemas"]["ModerationPostDetailApiSchema"];

/**
 * Un commentaire, vu par la modération.
 *
 * ⚠️ **Son contenu est conservé même supprimé**, contrairement à ce que rend
 * l'API publique. La suppression est le plus souvent la suite donnée à un
 * signalement : la masquer effacerait la preuve. D'où le barré à l'écran, et non
 * l'absence.
 */
export type ModerationComment =
  components["schemas"]["ModerationCommentApiSchemaOutput"];

/** Un cœur, avec le compte qui l'a posé. */
export type ModerationLike =
  components["schemas"]["ModerationLikeApiSchemaOutput"];

/**
 * Vingt par page.
 *
 * Le serveur plafonne à deux cents ; vingt tient sur un écran d'admin sans
 * demander de faire défiler la page entière avant d'atteindre la pagination.
 * Même valeur que la liste des événements, pour que les deux se parcourent
 * pareil.
 */
export const PAGE_SIZE = 20;

/**
 * La réponse brute du classifieur, telle que le serveur la range dans
 * `moderationScores`.
 *
 * ⚠️ **Le contrat la déclare `unknown`**, et c'est correct : c'est un `jsonb`
 * dont la forme appartient au service de modération, pas à l'API. On la relit
 * donc ici avec des gardes plutôt qu'en la castant — un `as` mentirait le jour
 * où le format évolue, et l'écran afficherait `undefined` sans rien signaler.
 */
export type ModerationScores = {
  model?: string;
  threshold?: number;
  evaluatedCategories?: string[];
  /** `null` veut dire « non jugé », **pas** « propre ». */
  highestScore?: number | null;
  perPhoto?: (Record<string, number> | null)[];
};
